/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import user from 'js/lib/user';

import type { Theme } from '@coursera/cds-core';
import { Grid, Typography2, VisuallyHidden, withTheme } from '@coursera/cds-core';

import { loadThreads } from 'bundles/discussions/actions/ThreadsActions';
import TopLevelForumsDropDown from 'bundles/discussions/components/forumsV2-ControlsBar/TopLevelForumsDropDown';
import { ForumDropDownWithTheme } from 'bundles/discussions/components/forumsV2-ForumDropDown/ForumDropDown';
import type { OnDemandCourseForumsV1 } from 'bundles/discussions/components/forumsV2/__types__';
import { loadingStates, questionSorts } from 'bundles/discussions/constants';
import type { LoadingState } from 'bundles/discussions/constants';
import type { Forum } from 'bundles/discussions/lib/types';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type OnDemandCourseForumStatisticsV1 from 'bundles/naptimejs/resources/onDemandCourseForumStatistics.v1';

import _t from 'i18n!nls/discussions';

import 'css!./__styles__/ControlsBar';

const { mostRecentSort, popularSort } = questionSorts;

const styles = {
  forumSelectorLabel: css`
    display: inline-block;
    padding-right: 8px;
  `,
  subForumSelectorLabel: css`
    display: inline-block;
    padding-right: 8px;
    white-space: nowrap;
  `,
  sortSelectorGridContainer: css`
    flex-flow: row;
    align-items: center;
    justify-content: flex-end;
    padding-right: 16px;
  `,
  gridItemWithRightPadding: css`
    padding-right: 8px;
  `,
  FilterSelectorLabel: css`
    white-space: nowrap;
    display: inline-block;
  `,
  filterSelectorGridContainer: css`
    flex-flow: row;
    align-items: center;
    justify-content: flex-end;
  `,
  controlsBarContainer: css`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    border-bottom: 1px solid var(--cds-color-grey-300);
    padding-bottom: var(--cds-spacing-100);
  `,
  controlsBarItem: css`
    display: flex;
    flex-flow: row;
    flex-wrap: wrap;
  `,
  filterWrapper: css`
    flex-flow: row;
  `,
  filterContainer: css`
    display: flex;
    flex-flow: row;
  `,
  displayInlineBlock: css`
    display: inline-block;
  `,
};

export type PropsControlsBar = {
  courseForums: OnDemandCourseForumsV1[];
  courseForumStatistics: OnDemandCourseForumStatisticsV1[];
  courseSlug: string;
  inSearch: boolean;
  currentForum: Forum;
  threadsListLoadingState: LoadingState;
  theme?: Theme;
};
function forumsByTypeName(forums: $TSFixMe, typeName: $TSFixMe): Forum[] {
  return forums.filter((forum: $TSFixMe) => forum.forumType.typeName === typeName);
}

function subForumsByParentForumId(forums: $TSFixMe, parentForumId: $TSFixMe): Forum[] {
  return forums.filter((forum: $TSFixMe) => forum.parentForumId === parentForumId);
}

export type ForumSelectorProps = {
  forums: Forum[];
  currentForum: Forum;
  courseSlug: string;
  discussionAllThreadsTabLinkCheck: boolean;
};

export const ForumSelector: React.FC<ForumSelectorProps> = ({
  forums,
  currentForum,
  courseSlug,
  discussionAllThreadsTabLinkCheck,
}) => {
  if (!forums || forums.length < 1 || discussionAllThreadsTabLinkCheck) {
    return null;
  }

  return (
    <Grid item className="rc-forumsV2-ControlsBar-ForumsDropDown">
      <Typography2 component="span" variant="subtitleMedium" css={styles.forumSelectorLabel}>
        {_t('Forum:')}
      </Typography2>
      <TopLevelForumsDropDown
        ariaLabelPrefix={_t('Forum: ')}
        currentForum={currentForum}
        courseForums={forums}
        selectedIndex={forums?.findIndex(({ id }) => currentForum.id === id)}
        courseSlug={courseSlug}
        skipTheme={true}
        param="forum"
      />
    </Grid>
  );
};

type SubForumSelectorProps = {
  subforums: Forum[];
  currentForum: Forum;
  courseSlug: string;
  executeAction: (action: any, payload: any) => void;
  routerParamsSort: string;
};

export const SubForumSelector: React.FC<SubForumSelectorProps> = ({
  subforums,
  currentForum,
  courseSlug,
  executeAction,
  routerParamsSort,
}) => {
  const userData = user.get();

  if (!subforums || subforums.length === 0) {
    return null;
  }

  return (
    <Grid item className="rc-forumsV2-ControlsBar-ForumsDropDown">
      <Typography2 component="span" variant="subtitleMedium" css={styles.subForumSelectorLabel}>
        {_t('Subforum:')}
      </Typography2>
      <TopLevelForumsDropDown
        ariaLabelPrefix={_t('Subforum: ')}
        currentForum={currentForum}
        courseForums={subforums}
        param="subForum_id"
        courseSlug={courseSlug}
        skipTheme={true}
        unsetOption={{ label: 'All', value: '', forum: currentForum, param: 'subForum_id' }}
        onChange={(forum, sort, pageNum, filterQueryString) => {
          executeAction(loadThreads, {
            filterQueryString,
            sort: routerParamsSort || 'lastActivityAtDesc',
            pageNum,
            forumType: forum.forumType.typeName,
            forumId: forum.id,
            userId: userData?.id,
            includeDeleted: false,
          });
        }}
      />
    </Grid>
  );
};

type SortSelectorProps = {
  threadsListLoadingState: LoadingState;
  routerParamSort: string;
};

export const SortSelector: React.FC<SortSelectorProps> = ({ threadsListLoadingState, routerParamSort }) => (
  <Grid container item css={styles.sortSelectorGridContainer}>
    <Grid item css={styles.gridItemWithRightPadding}>
      <Typography2 component="span" variant="subtitleMedium" css={styles.displayInlineBlock}>
        {_t('Sort:')}
      </Typography2>
    </Grid>
    <Grid item className="rc-forumsV2-ControlsBar-SortForumsDropDown">
      <ForumDropDownWithTheme<string, 'sort'>
        ariaLabelPrefix={_t('Sort: ')}
        css={styles.displayInlineBlock}
        style={{ width: 'max-content' }}
        options={[
          { value: mostRecentSort, param: 'sort', label: _t('Recent') },
          { value: popularSort, param: 'sort', label: _t('Top') },
        ]}
        e2eId="discussion-forums-sort"
      />
    </Grid>

    <VisuallyHidden role="status" aria-live="polite" component="span" className="rc-A11yScreenReaderOnly">
      {threadsListLoadingState === loadingStates.DONE && (
        <React.Fragment>
          {routerParamSort === 'popularityDesc'
            ? _t('Discussion forums threads are sorted by top')
            : _t('Discussion forums threads are sorted by most recent')}
        </React.Fragment>
      )}
    </VisuallyHidden>
  </Grid>
);

type FilterSelectorProps = {
  threadsListLoadingState: LoadingState;
  routerParamAnswered: string;
};

export const FilterSelector: React.FC<FilterSelectorProps> = ({ threadsListLoadingState, routerParamAnswered }) => (
  <Grid container item css={styles.filterSelectorGridContainer}>
    <Grid item css={styles.gridItemWithRightPadding}>
      <Typography2 component="span" variant="subtitleMedium" css={styles.FilterSelectorLabel}>
        {_t('Filter:')}
      </Typography2>
    </Grid>
    <Grid item className="rc-forumsV2-ControlsBar-FilterForumsDropDown">
      <ForumDropDownWithTheme<'true' | 'false' | '', 'answered'>
        ariaLabelPrefix={_t('Filter: ')}
        css={styles.displayInlineBlock}
        options={[
          { value: '', param: 'answered', label: _t('All') },
          { value: 'true', param: 'answered', label: _t('Answered') },
          { value: 'false', param: 'answered', label: _t('Unanswered') },
        ]}
        e2eId="discussion-forums-filter"
      />
    </Grid>

    <VisuallyHidden role="status" aria-live="polite" component="span" className="rc-A11yScreenReaderOnly">
      {threadsListLoadingState === loadingStates.DONE && (
        <React.Fragment>
          {routerParamAnswered === '' && _t('Discussion forums threads are filtered by all')}
          {routerParamAnswered === 'true' && _t('Discussion forums threads are filtered by answered')}
          {routerParamAnswered === 'false' && _t('Discussion forums threads are filtered by unanswered')}
        </React.Fragment>
      )}
    </VisuallyHidden>
  </Grid>
);

class _ControlsBar extends React.Component<PropsControlsBar> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
    executeAction: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof _ControlsBar.contextTypes>;

  render() {
    const classes = classNames('rc-forumsV2-ControlsBar', {
      'in-search-mode': this.props.inSearch,
    });

    const forums = forumsByTypeName(this.props.courseForums, this.props.currentForum.forumType.typeName);
    const subforums = subForumsByParentForumId(this.props.courseForums, this.props.currentForum.id);
    const discussionPath = window.location.pathname;

    const discussionAllThreadsTabLinkCheck = discussionPath.includes('discussions/all');
    const routerParamSort = this.context.router.location.query.sort;
    const routerParamAnswered = this.context.router.location.query.answered;
    const executeAction = this.context.executeAction;
    const routerParamsSort = this.context.router.params?.sort;

    return (
      <Grid container className={classes} css={styles.controlsBarContainer}>
        <Grid item css={styles.controlsBarItem}>
          <ForumSelector
            forums={forums}
            currentForum={this.props.currentForum}
            courseSlug={this.props.courseSlug}
            discussionAllThreadsTabLinkCheck={discussionAllThreadsTabLinkCheck}
          />
          <SubForumSelector
            subforums={subforums}
            currentForum={this.props.currentForum}
            courseSlug={this.props.courseSlug}
            executeAction={executeAction}
            routerParamsSort={routerParamsSort}
          />
        </Grid>
        <Grid item css={styles.filterWrapper}>
          <Grid container item css={styles.filterContainer}>
            <SortSelector
              threadsListLoadingState={this.props.threadsListLoadingState}
              routerParamSort={routerParamSort}
            />
            <FilterSelector
              threadsListLoadingState={this.props.threadsListLoadingState}
              routerParamAnswered={routerParamAnswered}
            />
          </Grid>
        </Grid>
      </Grid>
    );
  }
}

export const ControlsBar = withTheme(_ControlsBar);
